/**
 * The rules of אנשי חוץ — pure, and the only place they are stated.
 *
 * Two shapes, one person. {@link ExternalPerson} is who rides on *this* mission
 * and is carried by the team itself; {@link ExternalPersonRecord} is the card
 * kept in the directory so the same name does not have to be typed again next
 * month. This module is what turns each into the other, decides when two entries
 * are the same person, and spells the one sentence a temporary vehicle is
 * written as.
 *
 * Nothing here touches Dexie or the DOM: `src/db/external-people-store.ts` does
 * the filing, the assignment UI does the asking, and the healthcheck drives the
 * decisions in this module directly.
 */
import { CREW_ROLE_LABELS, EXTERNAL_PERSON_TAG, TEMPORARY_VEHICLE_LABEL } from '../types/assignment';
import type { CrewRole, ExternalPerson, ExternalPersonDraft } from '../types/assignment';
import type { ExternalPersonRecord, ExternalPersonRecordDraft } from '../types/external-person';

const trimmed = (value: string | null | undefined): string => String(value ?? '').trim();

/** An optional field — an empty string means "not recorded". */
const optional = (value: string | null | undefined): string | undefined => trimmed(value) || undefined;

export const newExternalPersonRecordId = (): string => `extp-${crypto.randomUUID()}`;

/** The seat an external person is placed in when the form was not asked. */
export const DEFAULT_EXTERNAL_CREW_ROLE: CrewRole = 'passenger';

// ── The temporary vehicle ────────────────────────────────────────────

/** True when this person arrives in a vehicle the unit does not own. */
export function hasTemporaryVehicle(person: Partial<ExternalPerson> | null | undefined): boolean {
  return Boolean(trimmed(person?.vehicleModel) || trimmed(person?.plateNumber));
}

/**
 * `פורד טרנזיט · 12-345-67` — the model and the plate, whichever were given.
 * Empty when neither was, so nothing ever prints a lone separator.
 */
export function temporaryVehicleLabel(person: Partial<ExternalPerson> | null | undefined): string {
  return [trimmed(person?.vehicleModel), trimmed(person?.plateNumber)].filter(Boolean).join(' · ');
}

/**
 * `רכב זמני: פורד טרנזיט · 12-345-67` — the whole line, as an order prints it.
 * The heading is never printed over an empty value.
 */
export function describeTemporaryVehicle(
  person: Partial<ExternalPerson> | null | undefined,
): string {
  const label = temporaryVehicleLabel(person);
  return label ? `${TEMPORARY_VEHICLE_LABEL}: ${label}` : '';
}

/** `איש חוץ · נהג` — the mark, with the seat it was placed in. */
export function externalPersonTag(crewRole?: CrewRole | null): string {
  const role = crewRole ? CREW_ROLE_LABELS[crewRole] : '';
  return role ? `${EXTERNAL_PERSON_TAG} · ${role}` : EXTERNAL_PERSON_TAG;
}

// ── Identity ─────────────────────────────────────────────────────────

/**
 * What makes two entries the same person, in the order the unit would answer
 * it: a מספר אישי if there is one, else a phone, else the name. Lower-cased and
 * stripped of the separators a phone is typed with, so `052-123-1234` and
 * `0521231234` are one person and not two cards.
 */
export function externalPersonKey(
  person: Partial<ExternalPerson> | Partial<ExternalPersonRecord> | null | undefined,
): string {
  const personalNumber = trimmed(person?.personalNumber).replace(/\D/g, '');
  if (personalNumber) return `pn:${personalNumber}`;
  const phone = trimmed(person?.phone).replace(/\D/g, '');
  if (phone) return `ph:${phone}`;
  return `nm:${trimmed(person?.fullName).toLocaleLowerCase('he')}`;
}

/**
 * Whether two entries are the same person.
 *
 * Deliberately not "same {@link externalPersonKey}": the key picks *one*
 * identifier by precedence, so a card filed with a מ.א. and a phone would never
 * match the same person entered next month with their phone alone — and the
 * unit would end up with two cards for one contractor. So each identifier is
 * compared only when *both* sides carry it, strongest first, and the name is
 * what settles it for somebody who carries neither number.
 */
export function isSameExternalPerson(
  a: Partial<ExternalPerson> | Partial<ExternalPersonRecord> | null | undefined,
  b: Partial<ExternalPerson> | Partial<ExternalPersonRecord> | null | undefined,
): boolean {
  const digits = (value: string | null | undefined): string => trimmed(value).replace(/\D/g, '');
  const personalA = digits(a?.personalNumber);
  const personalB = digits(b?.personalNumber);
  if (personalA && personalB) return personalA === personalB;
  const phoneA = digits(a?.phone);
  const phoneB = digits(b?.phone);
  if (phoneA && phoneB) return phoneA === phoneB;
  const nameA = trimmed(a?.fullName).toLocaleLowerCase('he');
  return Boolean(nameA) && nameA === trimmed(b?.fullName).toLocaleLowerCase('he');
}

/** The kept card this person already has, or `null` for somebody new. */
export function findExternalPersonMatch(
  records: readonly ExternalPersonRecord[],
  person: Partial<ExternalPerson> | null | undefined,
): ExternalPersonRecord | null {
  if (!trimmed(person?.fullName) && !trimmed(person?.phone) && !trimmed(person?.personalNumber)) {
    return null;
  }
  if (person?.directoryId) {
    const byId = records.find((record) => record.id === person.directoryId);
    if (byId) return byId;
  }
  return records.find((record) => isSameExternalPerson(record, person)) ?? null;
}

// ── Between the two shapes ───────────────────────────────────────────

/**
 * A kept card, taken out of the directory and placed on a mission. The
 * `directoryId` rides along, so re-saving the same person updates their card
 * instead of breeding a second one under a name spelled slightly differently.
 */
export function toExternalPerson(
  record: ExternalPersonRecord,
  id: string = record.id,
): ExternalPerson {
  return {
    id,
    fullName: trimmed(record.fullName),
    personalNumber: optional(record.personalNumber),
    role: optional(record.role),
    phone: optional(record.phone),
    notes: optional(record.notes),
    vehicleModel: optional(record.vehicleModel),
    plateNumber: optional(record.plateNumber),
    directoryId: record.id,
  };
}

/** A person on a mission, as the card the directory would keep of them. */
export function toExternalPersonDraft(
  person: ExternalPerson | ExternalPersonDraft,
  crewRole?: CrewRole,
): ExternalPersonRecordDraft {
  return {
    id: person.directoryId,
    fullName: trimmed(person.fullName),
    defaultCrewRole: crewRole,
    personalNumber: optional(person.personalNumber),
    phone: optional(person.phone),
    role: optional(person.role),
    vehicleModel: optional(person.vehicleModel),
    plateNumber: optional(person.plateNumber),
    notes: optional(person.notes),
  };
}

// ── The kept card ────────────────────────────────────────────────────

export interface NormalizeRecordContext {
  /** The card being replaced, when there is one — it keeps `createdAt`. */
  existing?: ExternalPersonRecord | null;
  /** Whether this write is also a placement, which is what `useCount` counts. */
  used?: boolean;
  now?: number;
}

/**
 * One directory card, cleaned up into the record the store persists.
 *
 * A field the form left empty never blanks one the card already held: a manager
 * who types a name and a phone for today's run must not wipe the plate somebody
 * recorded last month. `useCount` only moves when the write is a placement.
 */
export function normalizeExternalPersonRecord(
  draft: ExternalPersonRecordDraft,
  context: NormalizeRecordContext = {},
): ExternalPersonRecord {
  const existing = context.existing ?? null;
  const stamp = new Date(context.now ?? Date.now()).toISOString();
  const keep = (next: string | null | undefined, held: string | undefined): string | undefined =>
    optional(next) ?? held;

  return {
    id: draft.id ?? existing?.id ?? newExternalPersonRecordId(),
    fullName: trimmed(draft.fullName) || trimmed(existing?.fullName),
    defaultCrewRole: draft.defaultCrewRole ?? existing?.defaultCrewRole,
    personalNumber: keep(draft.personalNumber, existing?.personalNumber),
    phone: keep(draft.phone, existing?.phone),
    role: keep(draft.role, existing?.role),
    vehicleModel: keep(draft.vehicleModel, existing?.vehicleModel),
    plateNumber: keep(draft.plateNumber, existing?.plateNumber),
    notes: keep(draft.notes, existing?.notes),
    useCount: (existing?.useCount ?? 0) + (context.used === false ? 0 : 1),
    lastUsedAt: context.used === false ? existing?.lastUsedAt : stamp,
    createdAt: existing?.createdAt ?? stamp,
    updatedAt: stamp,
  };
}

/** True when the card is worth keeping at all — a card needs a name. */
export function isKeepableExternalPerson(
  draft: Pick<ExternalPersonRecordDraft, 'fullName'> | null | undefined,
): boolean {
  return trimmed(draft?.fullName).length > 0;
}

/**
 * The directory as the picker offers it: most recently placed first, then the
 * most used, then alphabetically — so the contractor who came last week is the
 * first name a manager sees, and the list never reorders itself at random.
 */
export function sortExternalPeople(
  records: readonly ExternalPersonRecord[],
): ExternalPersonRecord[] {
  return [...records].sort(
    (a, b) =>
      trimmed(b.lastUsedAt).localeCompare(trimmed(a.lastUsedAt)) ||
      (b.useCount ?? 0) - (a.useCount ?? 0) ||
      a.fullName.localeCompare(b.fullName, 'he'),
  );
}

/** `משה לוי — 052-123-1234 · פורד טרנזיט` — one line of the picker. */
export function describeExternalPersonRecord(record: ExternalPersonRecord): string {
  const numbers = [trimmed(record.phone), trimmed(record.personalNumber)]
    .filter(Boolean)
    .join(' / ');
  const rest = [numbers, trimmed(record.role), temporaryVehicleLabel(record)]
    .filter(Boolean)
    .join(' · ');
  return rest ? `${trimmed(record.fullName)} — ${rest}` : trimmed(record.fullName);
}
